import React from "react";
import { useTheme } from "styled-components";
import { VictoryLabel, VictoryPie } from "victory";

import Typography from "@/components/Typography";
import { moneyFormatterWithoutCurrency } from "@/utils/moneyFormat";

interface ExpensesGraphProps {
  period: string;
  paidExpenses: number;
  toPayExpenses: number;
  overdueExpenses: number;
  paidValue: number;
  toPayValue: number;
  overdueValue: number;
  isSPE?: boolean;
}

const ExpensesGraph = ({
  period,
  toPayExpenses,
  overdueExpenses,
  paidExpenses,
  paidValue,
  toPayValue,
  overdueValue,
  isSPE = false,
}: ExpensesGraphProps) => {
  const theme = useTheme();

  const totalExpenses = toPayExpenses + overdueExpenses + paidExpenses;

  const expensesData = [
    {
      name: "CONTA PAGA",
      value: paidExpenses,
      total: paidValue,
      color: theme.colors.tertiary300,
    },
    {
      name: "CONTA A PAGAR",
      value: toPayExpenses,
      total: toPayValue,
      color: theme.colors.warning200,
    },
    {
      name: "ATRASADAS",
      value: overdueExpenses,
      total: overdueValue,
      color: theme.colors.danger200,
    },
    {
      name: "TOTAL DE CONTAS",
      value: totalExpenses,
      color: theme.colors.grey700,
    },
  ];

  const graphData = () => {
    if (totalExpenses == 0) return [{ x: "Sem dados", y: 1 }];

    return [
      { x: "CONTA PAGA", y: paidExpenses },
      { x: "CONTA A PAGAR", y: toPayExpenses },
      { x: "ATRASADAS", y: overdueExpenses },
    ];
  };

  const labelRadius = ({ datum }: any) => {
    if (totalExpenses == 0) return 0.1;
    if (datum.xName === "ATRASADAS") return 70;

    return 50;
  };

  const label = ({ datum }: any) => {
    if (totalExpenses == 0) return "Sem Dados";
    if (datum.y === 0) return "";

    return `${((100 * datum.y) / totalExpenses).toFixed(0)}%`;
  };
  return (
    <>
      <Typography variant="h6" color={isSPE ? "black" : "grey500"}>
        Referente a {period}
      </Typography>
      <div className="grid grid-cols-1 md:grid-cols-5">
        <div className="flex justify-center md:col-span-3">
          <svg height={500} width={400} className="mr-0">
            <VictoryPie
              standalone={false}
              height={500}
              animate={{
                duration: 2000,
              }}
              colorScale={[
                theme.colors.tertiary300,
                theme.colors.warning200,
                theme.colors.danger200,
              ]}
              labelComponent={
                <VictoryLabel
                  style={{
                    font: "Inter",
                    fontWeight: "bold",
                    fill: theme.colors.black,
                  }}
                />
              }
              labels={({ datum }) => label({ datum })}
              labelRadius={({ datum }) => labelRadius({ datum })}
              data={graphData()}
            />
          </svg>
        </div>
        <div className="flex">
          <div className="my-auto flex flex-col gap-8">
            {expensesData.map((expense) => (
              <div key={expense.name} className="flex flex-row gap-6">
                <div className="h-16 w-2 rounded" style={{ backgroundColor: expense.color }} />
                <div className="flex flex-col">
                  <Typography variant="overline" color="white">
                    {expense.name}
                  </Typography>
                  <div className="flex flex-row items-baseline gap-2 whitespace-nowrap">
                    <Typography variant="h3" color="white">
                      {expense.value}
                    </Typography>
                    {expense.total !== undefined && (
                      <Typography variant="pLarge" color="white">
                        (R$ {moneyFormatterWithoutCurrency(expense.total)})
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExpensesGraph;
